<template>
    <div>
        <input
            id="taskEmailAttachment"
            ref="taskEmailAttachment"
            class="d-none"
            type="file"
            multiple
            name="taskEmailAttachment"
            @change="uploadChange"
        />
        <footer-titlebar
            icon="mdi-email-outline"
            :title="saveText"
            @close="$emit('close')"
        />
        <div class="pr-3 pl-10 pb-3">
            <Email
                ref="emailBlock"
                :to-recipients="toRecipients"
                :cc-recipients="ccRecipients"
                :bcc-recipients="bccRecipients"
                :content="content"
                :subject="subject"
                :files="form_data.files"
                :contact-tabs="contactTabs"
                :errors="errors"
                :template-id="templateId"
                :only-automations="onlyAutomations"
                :account-id="accountId"
                :selected-locale-code="localeCode"
                :is-cc-active="isCcActive"
                :is-bcc-active="isBccActive"
                :client-id="clientId"
                toolbar-id="#task-email-toolbar"
                :is-new-email="isNewEmail"
                :email-integration-id="emailIntegrationId"
                @update-email-integration-id="emailIntegrationId = $event"
                @clearTemplate="clearTemplate"
                @updateContent="updateContent"
                @updateSubject="updateSubject"
                @updateToRecipients="updateToRecipients"
                @updateCcRecipients="updateCcRecipients"
                @updateBccRecipients="updateBccRecipients"
                @deleteFile="fileUpload.delete_file"
                @toggleCc="isCcActive = !isCcActive"
                @toggleBcc="isBccActive = !isBccActive"
                @focusInfo="setFocusedField($event)"
            />
            <Automation
                v-if="isAutomation"
                :selected-days="selectedDays"
                :selected-trigger="selectedTrigger"
                :can-remove="!onlyAutomations"
                @updateSelectedDays="selectedDays = $event"
                @updateSelectedTrigger="selectedTrigger = $event"
                @removeAutomation="isAutomation = false"
            />
            <Footer
                :rich-text-editor="richTextEditor"
                :is-automation="isAutomation"
                :is-template="!!templateId"
                :is-new-email="isNewEmail"
                :placeholder-types="placeholderTypes"
                :company-id="accountId"
                toolbar-id="task-email-toolbar"
                @chooseTemplate="showChooseTemplateDialog = true"
                @addAttachment="taskEmailAttachment.click()"
                @addAutomation="isAutomation = true"
                @close="close"
                @send="send"
                @placeholder="addPlaceholder($event)"
                @placeholders="setAutoSignature($event)"
            />
            <TemplateChooseDialog
                :show-dialog="showChooseTemplateDialog"
                :account-id="accountId"
                :client-locale-code="clientLocaleCode"
                @close="showChooseTemplateDialog = false"
                @templateChosen="onTemplateChosen"
                @templateDeleted="onTemplateDeleted"
                @templateUpdated="onTemplateUpdated"
            />
        </div>
    </div>
</template>

<script>
import {$t} from "@/i18n";
import {computed, ref} from "@vue/composition-api";
import Email from "@/components/Emailing/EmailEdit/Email"
import Footer from "@/components/Emailing/EmailEdit/Footer"
import FileUpload from "../../../composables/FileUpload";
import TemplateChooseDialog from "@/components/Emailing/EmailEdit/TemplateChooseDialog";
import Automation from "@/components/Emailing/EmailEdit/Automation";
import {deepClone} from "../../../helpers/helpers";
import {TASK_AUTOMATION_STATUS, TASK_AUTOMATION_TRIGGER_TYPE} from "../../../helpers/constants";
import EmailAutomationModule from "../../../store/modules/email_automation";
import FooterTitlebar from "../FooterTitlebar";
import AppModule from "@/store/modules/app"
import ValidationModule from "@/modules/validation/validation"
import EmailAutomationValidationModule from "@/modules/emailing/emailAutomationValidation";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import {formatSysDateTime} from "@/plugins/formatting";
import EmailSignatureModule from "../../../api/email_automation/email_signature";

export default {
    name: "TaskAddEmailControl",
    components: {
        Email,
        Footer,
        TemplateChooseDialog,
        Automation,
        FooterTitlebar
    },
    props: {
        taskId: {
            type: [Number, String],
            required: true
        },
        accountId: {
            type: Number,
            required: true
        },
        clientId: {
            type: Number,
            default: null
        },
        contactTabs: {
            type: Array,
            required: true
        },
        automations: {
            type: Array,
            required: true
        },
        editingAutomationIndex: {
            type: Number,
            default: null
        },
        onlyAutomations: {
            type: Boolean,
            required: true
        },
        placeholderTypes: {
            type: Array,
            default: () => []
        },
        clients: {
            type: Array,
            default: () => []
        }
    },
    setup(props, ctx) {
        const {showFeedbackNotification} = FeedbackNotificationModule();
        const {emailAutomationTemplates} = EmailAutomationModule()
        const {getMemberDefaultSignature} = EmailSignatureModule()
        const appModule = AppModule()
        const task_data = ref({
            account_id: props.accountId,
            id: props.taskId,
            files: []
        })
        const form_data = ref({
            account_id: props.accountId,
            id: props.taskId,
            files: []
        })
        const selectedDays = ref(0);
        const selectedTrigger = ref(null);
        const showChooseTemplateDialog = ref(false);
        const taskEmailAttachment = ref(null);
        const emailBlock = ref(null);
        const isCcActive = ref(false);
        const isBccActive = ref(false);
        const errors = ref({});
        const toRecipients = ref([]);
        const ccRecipients = ref([]);
        const bccRecipients = ref([]);
        const subject = ref("");
        const content = ref("");
        const isAutomation = ref(false);
        const templateId = ref(null);
        const localeCode = ref(null);
        const emailIntegrationId = ref(null);
        const isNewEmail = ref(true);
        const focusInfo = ref({fieldName: "content", cursorIndex: null})
        const autoSignatureAdded = ref(false)

        if (props.onlyAutomations) {
            isAutomation.value = true
        }

        selectedTrigger.value = TASK_AUTOMATION_TRIGGER_TYPE.START_DATE

        const richTextEditor = computed(() => {
            if (emailBlock.value) {
                return emailBlock.value.$refs.richTextEditor
            }
        })

        const saveText = computed(() => {
            if (!isAutomation.value) {
                return $t("Send email")
            }

            return isNewEmail.value ? $t("Save automated email") : $t("Send email")
        })

        const close = () => {
            ctx.emit('close')
        }

        const getSaveDataRecipientsList = (type, recipients) => {
            let mappedRecipients = []
            for (let recipient of recipients) {
                if (recipient.custom_type) {
                    mappedRecipients.push({
                        name: recipient.name ? recipient.name : recipient.surname,
                        custom_value: recipient.custom_value,
                        custom_type: recipient.custom_type,
                        related_type: recipient.related_type,
                        type: type
                    })
                } else {
                    mappedRecipients.push({
                        name: recipient.name ? recipient.name : recipient.surname,
                        email: recipient.email,
                        type: type,
                        id: recipient.id
                    })
                }
            }

            return mappedRecipients
        }

        const getSaveDataRecipients = () => {
            let recipients = []
            recipients = recipients.concat(getSaveDataRecipientsList('to', toRecipients.value))
            recipients = recipients.concat(getSaveDataRecipientsList('cc', ccRecipients.value))
            recipients = recipients.concat(getSaveDataRecipientsList('bcc', bccRecipients.value))

            return recipients
        }

        const getSaveData = () => {
            const days = isAutomation.value ? selectedDays.value : 0
            const trigger = isAutomation.value ? selectedTrigger.value : ''
            const date = formatSysDateTime(new Date())
            const editingAutomation = props.editingAutomationIndex !== null ? props.automations[props.editingAutomationIndex] : null

            return {
                id: editingAutomation ? editingAutomation.id : null,
                content_template_id: templateId.value === -1 ? null : templateId.value,
                subject: templateId.value ? null : subject.value,
                content: templateId.value ? null : content.value,
                created_at: date,
                status: TASK_AUTOMATION_STATUS.NEW,
                attachments: form_data.value.files,
                trigger: trigger,
                days: days,
                locale_code: localeCode.value,
                email_integration_id: emailIntegrationId.value === -1 ? null : emailIntegrationId.value, // In case of client default we set it to null
                is_email_integration_client_default: emailIntegrationId.value === -1,
                data: {
                    recipients: getSaveDataRecipients()
                }
            }
        }

        const validateInput = () => {
            errors.value = {
                subject: subject.value ? null : $t('Required field'),
                content: content.value ? null : $t('Required field'),
                toRecipients: toRecipients.value.length > 0 ? null : $t('Required field'),
                toRecipientsValues: EmailAutomationValidationModule.validateRecipients(toRecipients.value),
                ccRecipientsValues: EmailAutomationValidationModule.validateRecipients(ccRecipients.value),
                bccRecipientsValues: EmailAutomationValidationModule.validateRecipients(bccRecipients.value)
            }
        }

        const send = async () => {
            // Recipient wont save if you dont unfocus, this is why we have timeout
            setTimeout(() => {
                errors.value = ValidationModule.clearErrors(errors.value)
                validateInput()
                if (ValidationModule.hasErrors(errors.value)) {
                    return
                }

                const automationsCopy = deepClone(props.automations)
                if (props.editingAutomationIndex !== null) {
                    automationsCopy[props.editingAutomationIndex] = getSaveData()
                } else {
                    automationsCopy.push(getSaveData())
                }

                ctx.emit('saveAutomation', automationsCopy)
                close()
            }, 0)
        }

        const fileDeleted = (deletedId) => {
            form_data.value.files = form_data.value.files.filter(x => x.id !== deletedId);
            task_data.value.files = task_data.value.files.filter(x => x.id !== deletedId);
        }
        const fileUpload = FileUpload("email_task", task_data, form_data, false, null, fileDeleted);
        const uploadChange = (e) => {
            fileUpload.add_files_to_upload(e.target.files, false);
        };

        const updateContent = (e) => {
            content.value = e
            errors.value.content = null;
        }

        const updateSubject = (e) => {
            subject.value = e;
            errors.value.subject = null;
        }

        const getTemplateRow = (contentTemplateId, localeCode) => {
            let template = emailAutomationTemplates.value.find(x => x.id === contentTemplateId)
            if (!template) {
                return
            }

            return template.rows.find(x => x.locale_code === localeCode)
        }

        const onTemplateChosen = (selectedTemplate) => {
            templateId.value = selectedTemplate.templateId
            localeCode.value = selectedTemplate.locale_code

            const templateRow = getTemplateRow(selectedTemplate.templateId, selectedTemplate.locale_code)

            subject.value = templateRow.subject
            content.value = templateRow.content

            showChooseTemplateDialog.value = false
        }

        const addNameToRecipientsWithContact = (recipients) => {
            let newRecipients = []

            for (let recipient of recipients) {
                for (let tab in props.contactTabs) {
                    for (let contact in tab.contacts) {
                        if (contact.id === recipient.id) {
                            recipient.name = contact.name
                        }
                    }
                }

                newRecipients.push(recipient)
            }

            return newRecipients
        }

        const loadEditingEmail = async () => {
            const editingAutomation = deepClone(props.automations[props.editingAutomationIndex])

            templateId.value = editingAutomation.content_template_id
            subject.value = editingAutomation.subject ? editingAutomation.subject : ''
            content.value = editingAutomation.content ? editingAutomation.content : ''

            if (editingAutomation.content_template_id) {
                localeCode.value = editingAutomation.locale_code;

                const templateRow = getTemplateRow(templateId.value, localeCode.value)
                if (templateRow) {
                    subject.value = templateRow.subject
                    content.value = templateRow.content
                }
            }

            const recipients = addNameToRecipientsWithContact(editingAutomation.data.recipients)

            emailIntegrationId.value = editingAutomation.is_email_integration_client_default ? -1 : editingAutomation.email_integration_id
            toRecipients.value = recipients.filter(x => x.type === 'to')
            ccRecipients.value = recipients.filter(x => x.type === 'cc')
            bccRecipients.value = recipients.filter(x => x.type === 'bcc')
            task_data.value.files = editingAutomation.attachments ? editingAutomation.attachments : []
            form_data.value.files = editingAutomation.attachments ? editingAutomation.attachments : []

            selectedDays.value = editingAutomation.days;
            selectedTrigger.value = editingAutomation.trigger;

            if (selectedTrigger.value && selectedTrigger.value !== '') {
                isAutomation.value = true
            }

            if (ccRecipients.value.length > 0) {
                isCcActive.value = true
            }

            if (bccRecipients.value.length > 0) {
                isBccActive.value = true
            }

            isNewEmail.value = false
        }

        const updateToRecipients = (e) => {
            toRecipients.value = e
            errors.value.toRecipients = null;
            errors.value.toRecipientsValues = null
        }

        const updateCcRecipients = (e) => {
            ccRecipients.value = e
            errors.value.ccRecipientsValues = null
        }

        const updateBccRecipients = (e) => {
            bccRecipients.value = e
            errors.value.bccRecipientsValues = null
        }

        if (props.editingAutomationIndex !== null) {
            loadEditingEmail()
        }

        const onTemplateDeleted = (template) => {
            if (template.id === templateId.value) {
                templateId.value = null
            }
        }

        const onTemplateUpdated = (template) => {
            if (template.id === templateId.value) {
                const row = template.rows.find(x => x.locale_code === localeCode.value)
                if (row && row.content && row.subject) {
                    content.value = row.content
                    subject.value = row.subject
                } else {
                    templateId.value = null
                }
            }
        }

        const clientLocaleCode = computed(() => {
            const client = appModule.clients.value[props.accountId].find(x => x.id === props.clientId)
            if (!client) {
                return null
            }

            return client.locale_code
        })

        const clearTemplate = () => {
            templateId.value = null

            showFeedbackNotification($t('Template unlinked'))
        }

        const addPlaceholder = (placeholder) => {
            if (!focusInfo.value) {
                return
            }
            const fieldName = focusInfo.value?.fieldName
            if (fieldName === "content") {
                let quill = richTextEditor.value.$refs.richTextEditorContent.quill
                let selection = quill.getSelection(true)
                quill.insertText(selection.index, placeholder.value)
            } else if (fieldName === "subject") {
                const cursorIndex = focusInfo.value.cursorIndex
                const firstPart = subject.value.slice(0, cursorIndex);
                const secondPart = subject.value.slice(cursorIndex);
                subject.value = firstPart + placeholder.value + secondPart
                const newCursorIndex = focusInfo.value.cursorIndex + placeholder.value.length
                focusInfo.value.cursorIndex = newCursorIndex

                // Set focus back to the subject field
                const inputElement = emailBlock.value.$refs.subjectTextField.$refs.input
                inputElement.focus()
                setTimeout(() => {
                    inputElement.setSelectionRange(newCursorIndex, newCursorIndex);
                }, 0);
            }
        }

        const setAutoSignature = (placeholders) => {
            if (!isNewEmail.value || autoSignatureAdded.value) {
                return
            }

            const signature_member_default = getMemberDefaultSignature(placeholders)
            if (signature_member_default && signature_member_default.value) {
                // set
                let quill = richTextEditor.value.$refs.richTextEditorContent.quill
                insertNewLinesToQuill(quill)
                quill.insertText(3, signature_member_default.value);
                autoSignatureAdded.value = true
            }
        }

        const insertNewLinesToQuill = (quill) => {
            const editorContent = quill.getText();

            const lines = editorContent.split('\n');
            const linesToAdd = 3 - lines.length

            for (let i = 0; i < linesToAdd; i++) {
                quill.insertText(0, '\n', 'user')
            }
        }

        const setFocusedField = (event) => {
            focusInfo.value = event
        }

        return {
            emailIntegrationId,
            isCcActive,
            isBccActive,
            emailBlock,
            richTextEditor,
            close,
            send,
            toRecipients,
            ccRecipients,
            bccRecipients,
            subject,
            content,
            uploadChange,
            taskEmailAttachment,
            task_data,
            form_data,
            fileUpload,
            errors,
            updateContent,
            updateSubject,
            onTemplateChosen,
            showChooseTemplateDialog,
            isAutomation,
            templateId,
            selectedDays,
            selectedTrigger,
            updateToRecipients,
            localeCode,
            onTemplateDeleted,
            onTemplateUpdated,
            clearTemplate,
            isNewEmail,
            saveText,
            clientLocaleCode,
            updateCcRecipients,
            updateBccRecipients,
            addPlaceholder,
            setFocusedField,
            setAutoSignature,
        };
    }
};
</script>
